import { Chip, Stack, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import useOpen from "utils/useOpen";
import PropertiesDialog from "./PropertiesDialog";
import propertyService from "services/properties-service";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";

export default function AllProperties({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [properties, setProperties] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddProperty(newElement) {
    const [response, data] = await propertyService.createProperty({
      ...newElement,
      options: newElement.options
        ? newElement.options.map((option) => {
            return {
              name: option.label,
              sequence: option.sequence,
              value: option.id,
            };
          })
        : null,
    });
    if (response.ok) {
      const [response, data] = await propertyService.getProperties(lastFilter);
      if (response.ok) {
        setProperties(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateProperty(newElement) {
    const [response, data] = await propertyService.updateProperty(
      newElement.id,
      {
        ...newElement,
        options: newElement.options
          ? newElement.options.map((option) => {
              return {
                name: option.label,
                sequence: option.sequence,
                value: option.id,
              };
            })
          : null,
      },
    );
    if (response.ok) {
      const [response, data] = await propertyService.getProperties(lastFilter);
      if (response.ok) {
        setProperties(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteProperty(id) {
    const [response, data] = await propertyService.removeProperty(id);
    if (response.ok) {
      const [response, data] = await propertyService.getProperties(lastFilter);
      if (response.ok) {
        setProperties(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!properties?.length)
      return properties?.length === 0
        ? [
            {
              name: label("no-properties-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return properties.map((property) => {
      const genericLabel = {
        number: "Number",
        boolean: "Yes / No",
        datetime: "Date / Time",
        "datetime-auto": "Auto Date/Time",
        audio: "Audio",
        image: "Picture",
        dropdown: "Dropdown",
        text: "Text",
      };

      return {
        name: <MDTypography variant="inherit">{property.name}</MDTypography>,
        referenceCode: (
          <MDTypography variant="inherit">
            {property.referenceCode || label("none")}
          </MDTypography>
        ),
        generic: (
          <MDTypography variant="inherit">
            {genericLabel[property.generic] || property.generic || "—"}
          </MDTypography>
        ),
        unit: (
          <MDTypography variant="inherit">
            {property.unit?.name || "—"}
          </MDTypography>
        ),
        minMax: (
          <MDTypography variant="inherit">
            {property.min != null && property.max != null
              ? `${property.min} – ${property.max}`
              : property.min != null
                ? `Min: ${property.min}`
                : property.max != null
                  ? `Max: ${property.max}`
                  : "—"}
          </MDTypography>
        ),
        account: (
          <MDTypography variant="inherit">
            {property.account?.name || label("all")}
          </MDTypography>
        ),

        baseEntity: (
          <MDTypography variant="inherit">
            {property.baseEntity?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!property.accountId && can("isAdmin")) || property.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        property,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        property,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [properties, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("type-singular"),
        accessor: "generic",
      },
      {
        Header: label("unit-singular"),
        accessor: "unit",
      },
      {
        Header: "Min / Max",
        accessor: "minMax",
      },

      {
        Header: "Base entity",
        accessor: "baseEntity",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteProperty(open.get("alert").property.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <PropertiesDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddProperty}
          onClose={() => open.change("create", false)}
          data={null}
        />
      ) : null}
      {open.get("update") ? (
        <PropertiesDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateProperty}
          onClose={() => open.change("update", false)}
          data={open.get("update").property}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("property-plural")}{" "}
                  {permissions.get ? `(${properties?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-property-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-properties"
                      internalLoading={!Boolean(properties)}
                      fullWidth
                      setData={setProperties}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(await propertyService.getProperties(request));
                      }}
                      label={label("search-properties-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(properties)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
